// src/components/desk/ContractSections.jsx — the contract BUILDER's PURE half: the helpers
// that turn the server's section LIST into the page's state and back into the ASSEMBLY it
// takes (core/CONTRACT_API_CONTRACT.md §Sections, migration 1041). No React here since the
// design build (2026-09-11): the renderers live in ./contract/ — `SectionRows.jsx` (the
// bottom sheet on a phone, the left rail on a desktop, and the slim rows under the values)
// and `ClauseEditor.jsx` (one section's clause text, opened on demand, one language at a
// time on a phone and side by side on a desktop).
//
// Owner ruling 2026-09-10, spoken: "we should make this contract buildable — remove this
// section, add this section; on the left side the agent has the list of all sections; if they
// don't use one, turn it off, and if needed later add it back. The texts already exist in
// English, Georgian, Russian — whenever they create this contract they may want to change the
// text itself: in every text field they should be able to edit the text. It must be modular."
//
// Two shapes, and the difference is the whole module:
//   • the LIST — what the server sends (draft and detail): every section with its clauses,
//     each clause carrying the text IN this contract beside its DEFAULT. This is the page's
//     state, verbatim plus client-side custom sections.
//   • the ASSEMBLY — what the server takes (create / rerender): only the DIFFERENCES from the
//     template — switched-off keys, rewritten clauses (per language, only where the text
//     differs from the default), custom sections. `sectionsAssembly()` is the ONE place that
//     derives it; nothing else may re-implement the diff.
// Rules that look like styling and are not:
//   • Numbers are COUNTED live over the enabled numbered sections, in server order — the
//     server renumbers the document the same way, so what the list shows is what prints.
//   • Mandatory sections (D/P/L/S) show a lock and no switch; they are generated or fixed
//     text, so they get no editor either.
//   • A switched-off section is dimmed in the list and ABSENT from the editors: editing text
//     that will not print is a control with nothing behind it (diagnosis rule 6).
//   • `{{key}}` placeholders in a clause are values from the form; they are shown as-is and
//     never stripped, and the hint says so. The editor does not offer new ones.
//   • Custom sections carry only text (the API takes text_ka/text_en, no title); they sit
//     after the section the agent had selected, or after the last numbered one.

import { t } from '../../lib/i18n.js'

import { L } from './contract/paper.js'
// The section→field rule is PURE and lives in src/lib so it can be tested without a renderer;
// re-exported here because this is the module every section consumer already imports.
export { fieldsHiddenBySections, markersIn } from '../../lib/contractSectionFields.js'

const LANG_COLS = ['ka', 'en', 'ru']

export const newCustomKey = () => 'c:' + Math.random().toString(36).slice(2, 8).padEnd(6, '0')

// The server's list (+ the stored assembly, for a custom section's `after`) → the page's
// state. A section's `enabled` and its clause texts come from the list; nothing is derived
// here that the list does not already say.
export function normalizeSections(list, assembly = null) {
  const stored = new Map((Array.isArray(assembly) ? assembly : []).filter(a => a?.custom && a.key).map(a => [a.key, a]))
  const out = []
  for (const s of list || []) {
    if (!s || !s.key) continue
    const prevNumbered = [...out].reverse().find(x => x.numbered && !x.custom)
    out.push({
      key: String(s.key),
      mandatory: !!s.mandatory,
      // 🔒 declared by the TEMPLATE (1164), carried through untouched: only an optional section
      // may be switched off, and an older server that sends neither leaves both undefined,
      // which is today's behaviour rather than a locked document.
      policy: s.policy || null,
      gated_by: s.gated_by || null,
      removable: s.removable,
      custom: !!s.custom,
      numbered: s.numbered !== false,
      enabled: (s.mandatory || s.removable === false) ? true : s.enabled !== false,
      sort: s.sort ?? out.length,
      after: s.custom ? (stored.get(s.key)?.after ?? s.after ?? prevNumbered?.key ?? null) : null,
      title_ka: s.title_ka || '', title_en: s.title_en || '',
      clauses: (s.clauses || []).map(c => ({
        clause_no: String(c.clause_no ?? s.key), kind: c.kind || 'clause', condition: c.condition || null,
        text_ka: c.text_ka ?? '', text_en: c.text_en ?? '', text_ru: c.text_ru ?? '',
        default_ka: c.default_ka ?? c.text_ka ?? '', default_en: c.default_en ?? c.text_en ?? '', default_ru: c.default_ru ?? c.text_ru ?? '',
      })),
    })
  }
  return out
}

// key → running number over the ENABLED numbered sections, in order. Off/unnumbered → null.
export function sectionNumbers(sections) {
  const m = new Map()
  let n = 0
  for (const s of sections || []) m.set(s.key, s.enabled && s.numbered ? ++n : null)
  return m
}

export const clauseEdited = (c, lang) => (c[`text_${lang}`] ?? '') !== (c[`default_${lang}`] ?? '')

// The ASSEMBLY (API §Sections): switched-off keys, rewritten clauses, custom sections. A
// language whose text equals its default is OMITTED so the server keeps the template's.
// The annex rides in the assembly under its own key so the choice SURVIVES a save: the server
// keeps the list verbatim on the version and ignores a key no clause carries (contract_sections
// listSections only reads it into its `off` set), so this costs no migration and no endpoint.
export const PHOTOS_SECTION_KEY = '__photos'
export function sectionsAssembly(sections, { photosOff = false } = {}) {
  const out = []
  if (photosOff) out.push({ key: PHOTOS_SECTION_KEY, enabled: false })
  for (const s of sections || []) {
    if (s.custom) {
      const c = s.clauses[0] || {}
      const row = { key: s.key, custom: true, after: s.after || null }
      for (const l of LANG_COLS) if ((c[`text_${l}`] || '').trim()) row[`text_${l}`] = c[`text_${l}`]
      out.push(row)
      continue
    }
    // a section the agent cannot switch off never contributes a switch-off to the assembly —
    // the server refuses it anyway, and sending one would make the stored assembly a lie
    if (!s.enabled && !s.mandatory && s.removable !== false) { out.push({ key: s.key, enabled: false }); continue }
    if (!s.enabled) continue
    for (const c of s.clauses) {
      const row = { clause_no: c.clause_no }
      let any = false
      for (const l of LANG_COLS) if (clauseEdited(c, l)) { row[`text_${l}`] = c[`text_${l}`]; any = true }
      if (any) out.push(row)
    }
  }
  return out
}

// A custom section after `afterKey` (a numbered, non-custom section), else after the last
// numbered one. Its one clause starts EMPTY in every language; the editor fills it.
export function addCustomSection(sections, afterKey = null) {
  const key = newCustomKey()
  let idx = sections.findIndex(s => s.key === afterKey && s.numbered)
  if (idx < 0) { for (let i = sections.length - 1; i >= 0; i--) if (sections[i].numbered) { idx = i; break } }
  const anchor = (() => { for (let i = idx; i >= 0; i--) if (sections[i].numbered && !sections[i].custom) return sections[i].key; return null })()
  const custom = {
    key, mandatory: false, custom: true, numbered: true, enabled: true, sort: null, after: anchor, title_ka: '', title_en: '',
    clauses: [{ clause_no: key, kind: 'clause', condition: null, text_ka: '', text_en: '', text_ru: '', default_ka: '', default_en: '', default_ru: '' }],
  }
  const next = sections.slice()
  next.splice(idx + 1, 0, custom)
  return { sections: next, key }
}

// `utilities_mode=included` → "კომუნალური = ქირაში შედის", resolved from the draft's field
// definitions where possible; the raw condition otherwise (never a blank).
export function conditionLabel(condition, fields) {
  if (!condition) return ''
  const m = String(condition).match(/^([a-z0-9_]+)\s*(=|!=)\s*(.+)$/i)
  if (!m) return String(condition)
  const f = (fields || []).find(x => x.key === m[1])
  if (!f) return String(condition)
  const opt = (f.options || []).find(o => String(o.value) === m[3])
  const val = opt ? (L(opt, 'label') || m[3]) : m[3] === 'true' ? '✓' : m[3] === 'false' ? '✗' : m[3]
  return `${L(f, 'label') || f.key} ${m[2] === '!=' ? '≠' : '='} ${val}`
}

export const sectionTitle = s => L(s, 'title') || (s.custom ? t('desk_contract_section_custom_title') : s.key)
export const editorId = key => `ct-sec-${key}`

// A section whose clause text differs from the template's in ANY language of the contract —
// the "edited ↺" mark on the slim section row. A custom section is never "edited": it has
// no default to differ from.
export function sectionEdited(s, langs) {
  if (!s || s.custom) return false
  return (s.clauses || []).some(c => (langs || []).some(l => clauseEdited(c, l)))
}

// The drift issues (API §"One contract, many languages": `{clause_no, kind, lang, detail}`)
// that name one of THIS section's clauses — drawn inline in its editor, beside the text that
// can fix them, and as a mark on its row.
export function sectionIssues(s, issues) {
  if (!s || !Array.isArray(issues) || issues.length === 0) return []
  const nos = new Set((s.clauses || []).map(c => String(c.clause_no)))
  return issues.filter(it => nos.has(String(it.clause_no)))
}

// A clause's text split into prose and `{{marker}}` runs, for the read-only rendering where
// the markers become chips: ["The rent is ", {marker:'rent'}, " per month"].
export function splitMarkers(text) {
  const out = []
  const re = /{{s*([a-z0-9_]+)s*}}/gi
  let last = 0, m
  const str = String(text ?? '')
  while ((m = re.exec(str))) {
    if (m.index > last) out.push(str.slice(last, m.index))
    out.push({ marker: m[1], raw: m[0] })
    last = m.index + m[0].length
  }
  if (last < str.length) out.push(str.slice(last))
  return out
}
